import {
  Drawer,
  Button,
  Typography,
  message,
  Form,
  InputNumber,
  Input,
  Row,
  Col,
  Select,
} from "antd"
import React, { useEffect, useState } from "react"
import { db } from "../../../firebase/config"
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore"
import { useCollection } from "../../../hooks/useCollection"
import { progressBarLoading } from "../../../components/Elements/LoadingSpinner"

const { Link } = Typography

export default function UpdateWarehouseMaterial({ data }) {
  const { data: materialSuppliers, loading: materialSupplierLoading } =
    useCollection("materialSuppliers")
  const [open, setOpen] = useState(false)
  const [form] = Form.useForm()
  const [selectedSupplier, setSelectedSupplier] = useState({})

  //updating state
  const [dataLoading, setDataLoading] = useState(false)

  //loading bar progress
  const [progress, setProgress] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [currentItem, setCurrentItem] = useState(0)
  const [materialFor, setMaterialFor] = useState("Labels")

  useEffect(() => {
    if (totalItems > 0) {
      setProgress((currentItem / totalItems) * 100)
    }
  }, [totalItems, currentItem])

  useEffect(() => {
    if (open) {
      //checks if its open
      form.setFieldsValue({
        //sets the field values from prop
        material: data.material,
        slogan: data.slogan,
        supplierMaterialName: data.supplierMaterialName,
        eurM2: data.eurM2,
        gM2: data.gM2,
        stock: data.stock,
        comment: data.comment,
        EAN: data.EAN,
        materialFor: data.materialFor,
      })
      //checks if there is a supplier
      if (data.supplier && Object.keys(data.supplier).length > 0) {
        setSelectedSupplier(data.supplier)
        form.setFieldsValue({
          //sets the field values from prop
          supplier: data.supplier.supplierName,
        })
      }
    }
  }, [open, form, data])
  const showDrawer = () => {
    setOpen(true)
  }
  const onClose = () => {
    setOpen(false)
  }

  //when a form finishes
  const onFinish = (values) => {
    updateMaterial(values)
  }

  //updates materials
  //updates rolls
  //sends loading
  const updateMaterial = async (formValues) => {
    setDataLoading(true)
    setProgress(0) // Reset progress

    // Get a reference to the material document we are updating
    const materialRef = doc(db, "materials", data.id)

    try {
      // --- 1. FETCH ALL NECESSARY DATA ---
      // Before starting the batch, we need to read all the data we're going to use.
      const [originalMaterialSnap, rollsSnap, ordersSnap, priceCalcSnap] = await Promise.all([
        getDoc(materialRef), // Get the current state of the material for history
        getDocs(query(collection(db, "rolls"), where("material.id", "==", data.id))),
        getDocs(query(collection(db, "orders"), where("material.id", "==", data.id))),
        getDocs(query(collection(db, "priceCalculation"), where("materialId", "==", data.id))),
      ])

      if (!originalMaterialSnap.exists()) {
        throw new Error("The material you are trying to update no longer exists.")
      }

      // --- 2. PREPARE THE BATCH ---
      // Initialize a write batch to group all our database changes
      const batch = writeBatch(db)

      // --- Stage 2a: Create the History Record ---
      const currentMaterialData = originalMaterialSnap.data()
      const historyData = {
        ...currentMaterialData, // Copy all fields from the material before the update
        originalMaterialId: data.id, // Add a reference to the original document
        archivedAt: serverTimestamp(), // Timestamp the history entry
      }
      // Create a reference for the new document in the history collection
      const historyDocRef = doc(collection(db, "materialsHistory"))
      batch.set(historyDocRef, historyData) // Add the history creation to the batch

      // --- Stage 2b: Update the Main Material Document ---
      const newMaterialData = {
        ...formValues,
        comment: formValues.comment || "",
        supplier: selectedSupplier,
        updated: serverTimestamp(),
        materialFor,
      }
      batch.update(materialRef, newMaterialData) // Add the main update to the batch

      // --- Stage 2c: Update All Related Documents ---
      const allDocsToUpdate = [...rollsSnap.docs, ...ordersSnap.docs, ...priceCalcSnap.docs]
      setTotalItems(allDocsToUpdate.length)

      allDocsToUpdate.forEach((document, idx) => {
        const docRef = document.ref
        const collectionName = docRef.parent.id // "rolls", "orders", or "priceCalculation"

        const payload =
          collectionName === "priceCalculation"
            ? { material: newMaterialData.material } // Only update the name string
            : { material: { ...newMaterialData, id: data.id } } // Update the full nested object

        batch.update(docRef, payload) // Add each related doc update to the batch
        setCurrentItem(idx + 1) // Update progress for a better user experience
      })

      // --- 3. COMMIT THE BATCH ---
      // Atomically commit all the staged writes. It's all or nothing.
      await batch.commit()

      message.success("Success! Material and all related documents were updated atomically.")
      setOpen(false)
      form.resetFields()
    } catch (err) {
      console.error("Atomic update failed:", err)
      message.error(`An error occurred: ${err.message}`)
    } finally {
      // Reset all loading states regardless of success or failure
      setDataLoading(false)
      setProgress(0)
      setTotalItems(0)
      setCurrentItem(0)
    }
  }

  //handles supplier change
  const changeSelectedSupplier = (value, object) => {
    setSelectedSupplier(object)
  }

  //if form failed
  const onFinishFailed = (errorInfo) => {
    message.error("Failed:", errorInfo)
  }

  //handle material for
  const handleMaterialFor = (value) => {
    setMaterialFor(value)
  }

  //returns loading if data is loading
  if (dataLoading) {
    return progressBarLoading(progress, currentItem, totalItems, "Updating rolls...")
  }

  return (
    <>
      <Link type="primary" onClick={showDrawer}>
        edit
      </Link>

      <Drawer title="Edit material" placement="left" width={600} onClose={onClose} open={open}>
        <Form
          form={form}
          name="basic"
          onFinish={onFinish}
          layout="vertical"
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          style={{ width: "100%" }}>
          <Row gutter={[5, 0]}>
            <Col span={12}>
              <Form.Item
                label="Material"
                name="material"
                rules={[
                  {
                    required: true,
                    message: "Please input Material Name",
                  },
                ]}>
                <Input style={{ width: "100%" }} placeholder="Enter material name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Material Slogan" name="slogan">
                <Input style={{ width: "100%" }} placeholder="Enter material name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Supplier Material Name" name="supplierMaterialName">
                <Input style={{ width: "100%" }} placeholder="Enter material name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Supplier Name" name="supplier">
                <Select
                  showSearch
                  style={{
                    width: "100%",
                  }}
                  loading={materialSupplierLoading}
                  onChange={changeSelectedSupplier}
                  placeholder="Search for supplier name"
                  optionFilterProp="children"
                  filterOption={(input, option) => (option?.label ?? "").includes(input)}
                  filterSort={(optionA, optionB) =>
                    (optionA?.label ?? "")
                      .toLowerCase()
                      .localeCompare((optionB?.label ?? "").toLowerCase())
                  }
                  options={materialSuppliers}
                  fieldNames={{ label: "supplierName", value: "id" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="EUR m2"
                name="eurM2"
                rules={[
                  {
                    required: true,
                    message: "Please input eurm2",
                  },
                ]}>
                <InputNumber style={{ width: "100%" }} placeholder="Enter eur m2" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Grams m2"
                name="gM2"
                rules={[
                  {
                    required: true,
                    message: "Please input gM2",
                  },
                ]}>
                <InputNumber style={{ width: "100%" }} placeholder="Enter grams per m2" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Material EAN Code"
                name="EAN"
                rules={[
                  {
                    required: true,
                    message: "Please input EAN CODE",
                  },
                ]}>
                <InputNumber style={{ width: "100%" }} placeholder="Enter EAN" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Material For" name="materialFor">
                <Select
                  style={{
                    width: "100%",
                  }}
                  placeholder="For what use the material is gonna be"
                  onChange={handleMaterialFor}
                  options={[
                    {
                      value: "Labels",
                      label: "Labels",
                    },
                    {
                      value: "Tapes",
                      label: "Tapes",
                    },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="In Stock?" name="stock">
                <Select>
                  <Select.Option value={true}>Yes</Select.Option>
                  <Select.Option value={false}>No</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="comment" label="Comment">
                <Input.TextArea name="comment" rows={2} placeholder="Material comment" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  )
}
